import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  X,
  XCircle,
} from "lucide-react";
import {
  CaptableRunSummary,
  MAX_BATCH_CINS,
  Stage4BackfillItemResult,
} from "../../api/dataPipeline";
import {
  useEnqueueStage4Backfill,
  useStage4BackfillStatus,
} from "../../queries/dataPipeline";

export interface Stage4BackfillCompany {
  cin: string;
  name: string | null;
  latestCaptableRun: CaptableRunSummary | null;
}

type Props = {
  companies: Stage4BackfillCompany[];
  onClose: () => void;
};

// A company can be backfilled only if it has a completed Stage-3 run that
// hasn't already produced a Stage-4 workbook.
function isEligible(c: Stage4BackfillCompany): boolean {
  return (
    c.latestCaptableRun?.status === "COMPLETED" &&
    !c.latestCaptableRun?.stage4Url
  );
}

function ineligibleReason(c: Stage4BackfillCompany): string {
  if (!c.latestCaptableRun) return "No captable run";
  if (c.latestCaptableRun.status !== "COMPLETED") return "No completed run";
  return "Stage 4 already generated";
}

function ResultCell({
  result,
}: {
  result: Stage4BackfillItemResult | undefined;
}) {
  if (!result)
    return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />;
  if (!result.ok) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-red-600 truncate max-w-[16rem]"
        title={result.error}
      >
        <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
        {result.error ?? "Failed"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
      {result.skipped ? "Already had Stage 4" : "Generated"}
      {result.stage4Url && (
        <a
          href={result.stage4Url}
          target="_blank"
          rel="noreferrer"
          className="underline text-emerald-700 hover:text-emerald-800"
        >
          view
        </a>
      )}
    </span>
  );
}

export const Stage4BackfillModal = ({ companies, onClose }: Props) => {
  // Same hard cap the captable batch-extract endpoint enforces.
  const batchCompanies = useMemo(
    () => companies.slice(0, MAX_BATCH_CINS),
    [companies],
  );
  const overflow = companies.length - batchCompanies.length;

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: startBackfill, isLoading: isStarting } =
    useEnqueueStage4Backfill();
  const { data: status } = useStage4BackfillStatus(jobId);

  const eligibleRows = batchCompanies.filter(isEligible);
  const ineligibleRows = batchCompanies.filter((c) => !isEligible(c));

  const included = (c: Stage4BackfillCompany) => !excluded.has(c.cin);
  const pending = eligibleRows.filter(included);

  const allIncluded = eligibleRows.length > 0 && eligibleRows.every(included);
  const toggleAllIncluded = () => {
    setExcluded(
      allIncluded ? new Set(eligibleRows.map((r) => r.cin)) : new Set(),
    );
  };
  const toggleExcluded = (cin: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(cin)) next.delete(cin);
      else next.add(cin);
      return next;
    });
  };

  const resultsByCin = useMemo(() => {
    const map: Record<string, Stage4BackfillItemResult> = {};
    status?.results.forEach((r) => {
      map[r.cin] = r;
    });
    return map;
  }, [status]);

  // Once the job finishes, refresh the grid so newly generated Stage-4 badges show up.
  useEffect(() => {
    if (status?.status === "COMPLETED") {
      queryClient.invalidateQueries("listS3Files");
    }
  }, [status?.status, queryClient]);

  const handleGenerate = async () => {
    if (!pending.length || jobId) return;
    setSubmitError(null);
    try {
      const data = await startBackfill({ cins: pending.map((r) => r.cin) });
      setJobId(data.jobId);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to start Stage-4 backfill.",
      );
    }
  };

  const handleClose = () => {
    if (status) {
      queryClient.invalidateQueries("listS3Files");
    }
    onClose();
  };

  const running = !!jobId && status?.status !== "COMPLETED";

  return (
    <Transition.Root show as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-3xl rounded-lg bg-white shadow-xl flex flex-col max-h-[85vh]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <Dialog.Title className="text-sm font-semibold text-gray-900">
                  Generate Stage 4 — {batchCompanies.length} compan
                  {batchCompanies.length === 1 ? "y" : "ies"}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-4 py-2 space-y-1 flex-shrink-0">
                <p className="text-xs text-gray-500">
                  Regenerates the Stage-4 onboarding workbook from each
                  company&apos;s latest completed Stage-3 Excel — for older runs
                  that finished before Stage 4 was auto-generated. Runs with no
                  Stage-3 file to read can&apos;t be backfilled and will be
                  reported as errors below.
                </p>
                {overflow > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Max {MAX_BATCH_CINS} companies per batch — the last{" "}
                    {overflow} selected compan{overflow === 1 ? "y" : "ies"}{" "}
                    {overflow === 1 ? "was" : "were"} left out.
                  </p>
                )}
                {submitError && (
                  <p className="text-xs text-red-600">{submitError}</p>
                )}
                {jobId && status && (
                  <p className="text-xs text-gray-600">
                    {status.status === "COMPLETED"
                      ? `Done — ${status.processed}/${status.total} processed.`
                      : `Processing… ${status.processed}/${status.total}`}
                  </p>
                )}
              </div>

              <div className="overflow-y-auto flex-1 px-4 pb-2">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-2 w-8">
                        <input
                          type="checkbox"
                          checked={allIncluded}
                          disabled={!!jobId}
                          onChange={toggleAllIncluded}
                        />
                      </th>
                      <th className="py-2 pr-3">Company</th>
                      <th className="py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleRows.map((r) => (
                      <tr key={r.cin} className="border-b border-gray-100">
                        <td className="py-2 pr-2">
                          <input
                            type="checkbox"
                            checked={included(r)}
                            disabled={!!jobId}
                            onChange={() => toggleExcluded(r.cin)}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <div
                            className={
                              r.name ? "text-gray-800" : "text-gray-400 italic"
                            }
                          >
                            {r.name || "Unknown"}
                          </div>
                          <div className="font-mono text-gray-400">{r.cin}</div>
                        </td>
                        <td className="py-2">
                          {jobId && included(r) ? (
                            <ResultCell result={resultsByCin[r.cin]} />
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {ineligibleRows.map((r) => (
                      <tr
                        key={r.cin}
                        className="border-b border-gray-100 opacity-60"
                      >
                        <td className="py-2 pr-2" />
                        <td className="py-2 pr-3">
                          <div
                            className={
                              r.name ? "text-gray-800" : "text-gray-400 italic"
                            }
                          >
                            {r.name || "Unknown"}
                          </div>
                          <div className="font-mono text-gray-400">{r.cin}</div>
                        </td>
                        <td className="py-2">
                          <span className="text-gray-400 italic">
                            {ineligibleReason(r)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 flex-shrink-0">
                <span className="text-xs text-gray-500">
                  {pending.length} eligible for Stage-4 generation
                </span>
                <button
                  onClick={handleGenerate}
                  disabled={isStarting || running || !pending.length}
                  className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                >
                  {isStarting || running ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {running
                    ? "Generating…"
                    : `Generate Stage 4 (${pending.length})`}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
